/**
 * Small text helpers shared by search (schools, cities) and the nickname
 * filter. Pure functions, no DOM.
 */

/**
 * Fold text for comparison: Unicode-normalised, diacritics removed, lower-case,
 * whitespace collapsed. "Hoërskool  Klipfontein" → "hoerskool klipfontein".
 */
export function foldText(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Trim and collapse runs of whitespace to single spaces. */
export function collapseSpaces(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

/**
 * Filter and order `items` for a type-ahead query: prefix matches (of the whole
 * text or any word) first, then substring matches, each group alphabetical.
 * An empty query returns every item alphabetically. Matching ignores case and
 * diacritics.
 */
export function rankMatches<T>(items: readonly T[], query: string, text: (item: T) => string, limit = Infinity): T[] {
  const q = foldText(query);
  const scored: { item: T; key: string; score: number }[] = [];
  for (const item of items) {
    const key = foldText(text(item));
    const score = matchScore(key, q);
    if (score !== null) scored.push({ item, key, score });
  }
  scored.sort((a, b) => a.score - b.score || a.key.localeCompare(b.key));
  return scored.slice(0, limit).map((s) => s.item);
}

/** 0 = prefix of the text, 1 = prefix of a later word, 2 = substring, null = no match. */
function matchScore(folded: string, q: string): number | null {
  if (!q) return 0;
  if (folded.startsWith(q)) return 0;
  if (folded.split(/[\s-]+/).some((w) => w.startsWith(q))) return 1;
  return folded.includes(q) ? 2 : null;
}
